import React, { useState, useEffect, useRef } from "react";

interface DebouncedTextFieldProps {
  label: string;
  name: string;
  value: string;
  placeholder?: string;
  onInput: (event: React.FormEvent<HTMLInputElement>) => void;
  debounceMs?: number;
}

export const DebouncedTextField: React.FC<DebouncedTextFieldProps> = ({
  label,
  name,
  value,
  placeholder,
  onInput,
  debounceMs = 300,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  // Sync local state when external value changes (e.g. undo/redo)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // We use a generic type here because s-text-field events might differ slightly from standard React Input events
  // but they both respect currentTarget.value
  const handleChange = (e: { currentTarget: { value: string } }) => {
    const newValue = e.currentTarget.value;
    setLocalValue(newValue);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      // Mocking the event structure for the parent handler
      const mockEvent = {
        currentTarget: { value: newValue },
      } as React.FormEvent<HTMLInputElement>;
      onInput(mockEvent);
    }, debounceMs);
  };

  const handleBlur = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    const mockEvent = {
      currentTarget: { value: localValue },
    } as React.FormEvent<HTMLInputElement>;
    onInput(mockEvent);
  };

  return (
    <s-text-field
      label={label}
      name={name}
      value={localValue}
      placeholder={placeholder}
      onInput={handleChange}
      onBlur={handleBlur}
    />
  );
};
